"use client"

// React imports
import React from "react"

// External SDK imports
import { useBetsSummary, useChain } from "@azuro-org/sdk"
import { Message } from "@locmod/intl"
import { openModal } from "@locmod/modal"
import { useWallet } from "wallet"
import { useBalance } from "wagmi"

// Utility imports
import cx from "classnames"
import { toLocaleString } from "helpers"
import { base, baseSepolia } from "viem/chains"
import { type Address } from "viem"

// Internal hooks and utilities
import { useBalanceContext } from "src/contexts/BalanceContext"
import { useDetailedOvertimeBetslip } from "@/contexts/DetailedOvertimeBetslipContext"
import { useUnredeemedOvertimeTickets } from "@/hooks"
import useChainProviderHook from "@/hooks/useChainProvider"

// Components
import { Icon } from "components/ui"
import { Dropdown } from "components/inputs"

// Local imports
import messages from "./messages"
import ChainSelect from "./ChainSelect"
import ChainCurrency from "./ChainCurrency"

const BalanceInfo: React.FC<{
  nativeBalanceData: NativeBalance
  isNativeBalanceFetching: boolean
  tokenBalanceData: NativeBalance
  isTokenBalanceFetching: boolean
  selectedCollateralIndex?: number
  activeFreebets: any[]
  isFreebetsLoading: boolean
}> = ({
  nativeBalanceData,
  isNativeBalanceFetching,
  tokenBalanceData,
  isTokenBalanceFetching,
  selectedCollateralIndex,
  activeFreebets,
  isFreebetsLoading,
}) => {
  const { account: address } = useWallet()
  const { isOvertimeChain, isAzuroChain, isSXChain, betTokenInfo } =
    useChainProviderHook()
  const { appChain } = useChain()

  const { data: betsSummaryData, isFetching: isBetsSummaryFetching } =
    useBetsSummary({
      account: address!,
      query: { enabled: isAzuroChain },
    })

  const {
    data: OvertimeBetsSummaryData,
    isFetching: OvertimeIsBetsSummaryFetching,
  } = useUnredeemedOvertimeTickets({
    address,
    selectedCollateralIndex,
    enabled: isOvertimeChain,
  })

  // const azuroFreebets = useDetailedBetslip()

  // console.log({ azuroFreebets })

  // Determine the provider and return appropriate data
  const getProviderData = () => {
    switch (true) {
      case isOvertimeChain:
        return {
          betSummary: OvertimeBetsSummaryData,
          isFetching: OvertimeIsBetsSummaryFetching,
        }

      case isSXChain:
        return {
          betSummary: null,
          isFetching: false,
        }

      case isAzuroChain:
        return {
          betSummary: betsSummaryData,
          isFetching: isBetsSummaryFetching,
        }

      default:
        // Fallback to Azuro data
        return {
          betSummary: betsSummaryData,
          isFetching: isBetsSummaryFetching,
        }
    }
  }

  const { betSummary, isFetching } = getProviderData()

  const { inBets, toPayout } = betSummary || {}

  const tokenBalance = tokenBalanceData?.formatted
  const tokenSymbol =
    betTokenInfo?.symbol === "USDT0" ? "USDT" : betTokenInfo?.symbol

  const nativeBalance = nativeBalanceData?.formatted
  const nativeSymbol = nativeBalanceData?.symbol

  const formatedDecimals =
    appChain.id === base.id || appChain.id === baseSepolia.id ? 4 : 2

  return (
    <div className="rounded-md bg-accent-grey-900 overflow-hidden">
      <div className="py-2 px-3 border-b border-b-accent-grey-850">
        <Message
          className="text-caption-13 text-accent-grey-200 mb-[2px]"
          value={messages.balance}
        />
        <div className="space-x-1">
          {Boolean(isTokenBalanceFetching || isNativeBalanceFetching) ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <>
              <span className="text-caption-13 font-semibold">
                {toLocaleString(tokenBalance || 0, {
                  digits: formatedDecimals,
                })}{" "}
                {tokenSymbol}
              </span>
              <span className="text-caption-12 text-accent-grey-200">
                +{" "}
                {toLocaleString(nativeBalance || 0, {
                  digits: formatedDecimals,
                })}{" "}
                {nativeSymbol}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center">
        <div className="w-full py-2 px-3">
          <Message
            className="text-caption-13 text-accent-grey-200 mb-[2px]"
            value={messages.inBets}
            tag="p"
          />
          {isFetching ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <div className="text-caption-13 font-semibold">
              {toLocaleString(inBets || 0, { digits: formatedDecimals })}{" "}
              {tokenSymbol}
            </div>
          )}
        </div>
        <div className="w-full border-l border-l-accent-grey-850 py-2 px-3">
          <Message
            className="text-caption-13 text-accent-grey-200 mb-[2px]"
            value={messages.toRedeem}
            tag="p"
          />
          {isFetching ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <div className="text-caption-13 font-semibold">
              {toLocaleString(toPayout || 0, { digits: formatedDecimals })}{" "}
              {tokenSymbol}
            </div>
          )}
        </div>
      </div>
      {isAzuroChain && (
        <div className="flex items-center border-t border-t-accent-grey-850">
          <div className="w-full  py-2 px-3">
            <div className="flex items-center gap-1 mb-0.5">
              <Message
                className="text-caption-13 text-accent-grey-200"
                value={messages.freebet}
                tag="p"
              />
              <Icon name="interface/freebet" className="w-4 h-4" />
            </div>
            {isFreebetsLoading ? (
              <div className="bone h-4 w-10 rounded-full" />
            ) : (
              <div className="text-caption-13 font-semibold">
                {toLocaleString(
                  activeFreebets.reduce(
                    (sum, freebet) => sum + Number(freebet.amount || 0),
                    0
                  ),
                  { digits: formatedDecimals }
                )}{" "}
                {tokenSymbol}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Freebet section - now using data from NewFreeBetsChecker */}
    </div>
  )
}

const Content: React.FC<{
  close?: () => void
  nativeBalanceData: NativeBalance
  isNativeBalanceFetching: boolean
  tokenBalanceData: NativeBalance
  isTokenBalanceFetching: boolean
  isAAWallet: boolean
  selectedCollateralIndex?: number
  activeFreebets: any[]
  isFreebetsLoading: boolean
}> = ({
  close,
  nativeBalanceData,
  isNativeBalanceFetching,
  tokenBalanceData,
  isTokenBalanceFetching,
  isAAWallet,
  selectedCollateralIndex,
  activeFreebets,
  isFreebetsLoading,
}) => {
  return (
    <div className=" p-2 ds:w-[18.75rem] bg-accent-grey-850 rounded-md overflow-hidden space-y-2">
      <ChainSelect close={close} isAAWallet={isAAWallet} />

      <BalanceInfo
        nativeBalanceData={nativeBalanceData}
        isNativeBalanceFetching={isNativeBalanceFetching}
        tokenBalanceData={tokenBalanceData}
        isTokenBalanceFetching={isTokenBalanceFetching}
        selectedCollateralIndex={selectedCollateralIndex}
        activeFreebets={activeFreebets}
        isFreebetsLoading={isFreebetsLoading}
      />
    </div>
  )
}

type NativeBalance = {
  value: bigint
  decimals: number
  symbol: string
  formatted: string
}

const Balance = () => {
  const { account, isAAWallet } = useWallet()
  const { appChain } = useChain()
  const { setRefetchFunction, activeFreebets, isFreebetsLoading } =
    useBalanceContext()

  // Use shared token selection context
  const { betTokenInfo, isOvertimeChain } = useChainProviderHook()
  // Get selected collateral index for Overtime chains
  const { payment } = useDetailedOvertimeBetslip()
  const selectedCollateralIndex = isOvertimeChain
    ? payment.selectedCollateralIndex
    : undefined

  const {
    data: nativeBalanceData,
    isFetching: isNativeBalanceFetching,
    refetch: refetchNativeBalance,
  } = useBalance({
    address: account,
  }) as {
    data: NativeBalance
    isFetching: boolean
    refetch: () => void
  }

  const formatedDecimals =
    appChain.id === base.id || appChain.id === baseSepolia.id ? 4 : 2

  const {
    data: balanceData,
    isFetching: isBalanceFetching,
    refetch: refetchTokenBalance,
  } = useBalance({
    address: account,
    token: betTokenInfo?.address as Address,
  })

  // Register refetch functions with the context
  React.useEffect(() => {
    const combinedRefetch = () => {
      refetchNativeBalance()
      refetchTokenBalance()
    }
    setRefetchFunction(combinedRefetch)
  }, [refetchNativeBalance, refetchTokenBalance, setRefetchFunction])

  const tokenBalance = balanceData?.formatted
  const handleGetTokensClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    openModal("FundingModal")
  }

  const rootClassName = cx(
    "wd:h-10 -wd:h-8 bg-accent-grey-850 flex items-center justify-between px-1.5 mb:px-1 w-fit text-accent-grey-200 ui-open:text-accent-grey-30 hover:text-accent-grey-30 ui-open:bg-accent-grey-850 hover:bg-accent-grey-850 ui-open:border-grey-20 hover:border-grey-20 transition-all mb:w-full rounded-xl py-1"
  )

  return (
    <Dropdown
      className={cx("group")}
      contentClassName="mb:p-0"
      content={({ close }) => (
        <Content
          close={close}
          nativeBalanceData={nativeBalanceData}
          isNativeBalanceFetching={isNativeBalanceFetching}
          tokenBalanceData={balanceData as NativeBalance}
          isTokenBalanceFetching={isBalanceFetching}
          isAAWallet={isAAWallet}
          selectedCollateralIndex={selectedCollateralIndex}
          activeFreebets={activeFreebets}
          isFreebetsLoading={isFreebetsLoading}
        />
      )}
      placement="bottomRight"
    >
      <div className={rootClassName}>
        <div className="flex items-center">
          <ChainCurrency
            className="mr-2"
            chainClassName="border-accent-grey-900 group-hover:border-grey-10 ui-open:border-grey-10"
            chainId={betTokenInfo?.chainId}
            size={5}
            currencyIcon={betTokenInfo?.currencyIcon}
          />
          {isBalanceFetching ? (
            <div className="h-4 w-16 bg-accent-grey-800 rounded animate-pulse" />
          ) : (
            <div className="text-caption-13 min-w-[1rem]">
              {toLocaleString(tokenBalance || 0, { digits: formatedDecimals })}
            </div>
          )}
          <Icon
            className="size-4 ui-open:rotate-180"
            name="interface/caret_down"
          />
        </div>
        <div
          className="flex items-center justify-center rounded-min bg-brand-light-500 ml-2 cursor-pointer text-accent-grey-900 flex-none text-[13px] p-1 font-bold uppercase"
          onClick={handleGetTokensClick}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
        >
          <Message value={messages.deposit} />
        </div>
      </div>
    </Dropdown>
  )
}

export default Balance
